import React, { type FC, type ReactNode, useCallback } from 'react';
import { styled as styled2 } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/glyph/cross';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { Text } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link';
import { borderRadius } from '@atlassian/jira-common-styles/src';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	FireUiAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { cardBoxShadow, cardBoxHoverShadow } from '../../constants';
import messages from './messages';
import type { Props } from './types';

export const SAC_HELP_URL =
	'https://support.atlassian.com/jira-software-cloud/docs/reference-issues-in-your-development-work/';

export const MARKETPLACE_CONFIG_URL = '/plugins/servlet/upm';
export const BITBUCKET_CONFIG_URL = '/secure/admin/ConfigureDvcsOrganizations!default.jspa';

/**
 * TEMPORARY COMPONENT
 * Cleaning up tracked in ticket: https://softwareteams.atlassian.net/browse/ARC-1838
 */
const AppConfigLinkNew: FC<{
	isBitbucketProvider: boolean;
	isJiraGlobalAdmin: boolean;
	providerName: string;
	children: ReactNode;
}> = ({ isBitbucketProvider, isJiraGlobalAdmin, providerName, children }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	let configLink = isBitbucketProvider ? BITBUCKET_CONFIG_URL : MARKETPLACE_CONFIG_URL;
	let eventName = isBitbucketProvider ? 'manageBitbucketAccountsClick' : 'providerClick';

	if (providerName.toLowerCase() === 'github') {
		eventName = 'emptyStateCardForGHConnectLink';
		configLink = '/plugins/servlet/ac/com.github.integration.production/github-post-install-page';
	}

	if (!isJiraGlobalAdmin) {
		return <>{children}</>;
	}
	const configLinkClick = () => {
		fireUIAnalytics(createAnalyticsEvent({ action: 'clicked', actionSubject: 'link' }), eventName);
	};
	// eslint-disable-next-line jira/styled/styled-component-order, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const CardLink = styled2(Link)({
		position: 'relative',
		zIndex: 1,
	});
	return (
		<CardLink onClick={configLinkClick} href={configLink}>
			{children}
		</CardLink>
	);
};

const EmptyStateCard = ({
	simplified,
	projectId,
	providerName,
	url,
	setDismissedCards,
	isJiraGlobalAdmin,
	isBitbucketProvider,
	hasOtherRepositories,
}: Props) => {
	const { formatMessage } = useIntl();

	const onClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
		(_, analyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'codeEmptyStateTile', {
				nextgen: simplified,
				projectId,
				providerName,
			});
		},
		[simplified, projectId, providerName],
	);
	const onDismissClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
		(event, analyticsEvent) => {
			event.preventDefault();
			event.stopPropagation();
			setDismissedCards?.();
			fireUIAnalytics(analyticsEvent, 'codeEmptyStateDismiss', {
				nextgen: simplified,
				projectId,
				providerName,
			});
		},
		[simplified, projectId, providerName, setDismissedCards],
	);

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const EmptyStateCardWrapperLink = styled2(Link)({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		borderRadius: `${borderRadius}px`,
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		overflow: 'hidden',
		textIndent: '100%',
		whiteSpace: 'nowrap',
		position: 'absolute',
		'&:hover, &:visited, &:focus, &:active': {
			outline: 'none',
			textDecoration: 'none',
		},
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const EmptyStateCardContainer = styled2.div({
		position: 'relative',
		boxSizing: 'border-box',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		borderRadius: `${borderRadius}px`,
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface.raised', colors.P50),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		boxShadow: token('elevation.shadow.raised', cardBoxShadow),
		padding: token('space.100', '8px'),
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			boxShadow: token('elevation.shadow.overlay', cardBoxHoverShadow),
			transition: 'box-shadow 0.2s ease',
		},
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const EmptyStateDescription = styled2.span({
		boxSizing: 'border-box',
		font: token('font.body.UNSAFE_small'),
		textAlign: 'left',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text', colors.N800),
		overflow: 'hidden',
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const CardHeaderContainer = styled2.div({
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		marginBottom: token('space.100', '8px'),
		marginRight: token('space.200', '16px'),
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const CodeIconContainer = styled2.div({
		display: 'flex',
		flex: '0 0 auto',
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const EmptyStateCTALinkContent = styled2.div({
		boxSizing: 'border-box',
		overflow: 'hidden',
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const EmptyStateDescriptionContainer = styled2.div({
		boxSizing: 'border-box',
		marginBottom: token('space.100', '8px'),
		margin: `0 ${token('space.150', '12px')} ${token('space.150', '12px')} ${token(
			'space.400',
			'32px',
		)}`,
		height: '60px',
		overflow: 'hidden',
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const EmptyStateCardDismissButton = styled2.span({
		position: 'absolute',
		top: token('space.100', '8px'),
		right: token('space.100', '8px'),
		zIndex: 1,
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const CardLink = styled2(Link)({
		position: 'relative',
		zIndex: 1,
	});

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	const RepositoryProviderTypeName = styled2.div({
		flex: '1 1 auto',
		margin: '0',
		marginLeft: token('space.100', '8px'),
		boxSizing: 'border-box',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	});

	return (
		<EmptyStateCardContainer>
			<EmptyStateCardWrapperLink href={url} onClick={onClick} target="_blank" />
			<EmptyStateCTALinkContent>
				<CardHeaderContainer>
					<CodeIconContainer>
						<QuestionCircleIcon
							label=""
							size="medium"
							primaryColor={token('color.icon.subtle', colors.P500)}
							secondaryColor={token('elevation.surface.overlay', colors.P50)}
						/>
					</CodeIconContainer>
					<Heading as="div" size="xsmall">
						<RepositoryProviderTypeName>
							<Tooltip
								content={formatMessage(messages.title, {
									provider: providerName,
								})}
								tag="span"
							>
								<Text>{formatMessage(messages.title, { provider: providerName })}</Text>
							</Tooltip>
						</RepositoryProviderTypeName>
					</Heading>
				</CardHeaderContainer>
				<EmptyStateDescriptionContainer>
					<Tooltip
						content={
							<FormattedMessage
								{...messages.description}
								values={{
									sac: (chunks: ReactNode) => chunks,
									config: (chunks: ReactNode) => chunks,
								}}
							/>
						}
					>
						<EmptyStateDescription>
							<FormattedMessage
								{...messages.description}
								values={{
									sac: (chunks: ReactNode) => <CardLink href={SAC_HELP_URL}>{chunks}</CardLink>,
									config: (chunks: ReactNode) => (
										<>
											{providerName.toLowerCase() === 'github' && (
												<FireUiAnalytics eventName="viewed emptyStateCardForGitHub" />
											)}
											<AppConfigLinkNew
												isBitbucketProvider={isBitbucketProvider}
												isJiraGlobalAdmin={isJiraGlobalAdmin}
												providerName={providerName}
											>
												{chunks}
											</AppConfigLinkNew>
										</>
									),
								}}
							/>
						</EmptyStateDescription>
					</Tooltip>
				</EmptyStateDescriptionContainer>
			</EmptyStateCTALinkContent>
			{hasOtherRepositories && (
				<EmptyStateCardDismissButton>
					<Button
						iconBefore={
							<CloseIcon
								primaryColor={token('color.icon', colors.N600)}
								size="small"
								label={formatMessage(messages.dismissButtonLabel)}
							/>
						}
						appearance="subtle"
						spacing="none"
						label={formatMessage(messages.dismissButtonLabel)}
						onClick={onDismissClick}
					/>
				</EmptyStateCardDismissButton>
			)}
		</EmptyStateCardContainer>
	);
};
EmptyStateCard.displayName = 'EmptyStateCard';

export default EmptyStateCard;
